import React, { useEffect, useState } from 'react';
import { AlertCircle, Download, RefreshCw, Search, CheckCircle, Bug } from 'lucide-react';
import API from '../services/apiBase';

const UnmappedAccountsPage = () => {
  const [glList, setGlList] = useState([]);
  const [selectedGl, setSelectedGl] = useState('');
  const [validationData, setValidationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('amount'); // 'amount' or 'account'
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const loadGLList = async () => {
    try {
      const res = await fetch(`${API}/gl/list?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setGlList(data.gl || []);
        if (!selectedGl && data.gl && data.gl.length > 0) {
          setSelectedGl(data.gl[0].gl_id);
          // Auto-validate the first GL
          validateGL(data.gl[0].gl_id);
        }
      }
    } catch (e) {
      console.error('Failed to load GL list:', e);
    }
  };

  const validateGL = async (glId) => {
    if (!glId) return;

    setLoading(true);
    try {
      // Use the GL Calculations validation endpoint which checks against Rules.xlsx
      const res = await fetch(`${API}/gl-calcs/validation/${glId}`);
      if (res.ok) {
        const data = await res.json();
        setValidationData(data);
      }
    } catch (e) {
      console.error('Validation failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const runDebug = async () => {
    if (!selectedGl) return;
    setDebugLoading(true);
    setDebugData(null);
    setShowDebug(false);
    try {
      const res = await fetch(`${API}/gl-calcs/validation/${selectedGl}?debug=true`);
      const data = await res.json();
      // If _debug key is present use it; otherwise show the whole response so we can diagnose
      setDebugData(data._debug || { _note: 'No _debug key returned', _status: res.status, _raw: data });
      setShowDebug(true);
    } catch (e) {
      setDebugData({ _error: String(e) });
      setShowDebug(true);
    } finally {
      setDebugLoading(false);
    }
  };

  useEffect(() => {
    loadGLList();
  }, []);

  const handleGlChange = (glId) => {
    setSelectedGl(glId);
    validateGL(glId);
  };

  const exportUnmapped = () => {
    if (!validationData || !validationData.unmapped_accounts.length) {
      alert('No unmapped accounts to export');
      return;
    }

    const csv = ['Account Number,Account Name,Description,Total Amount'].concat(
      validationData.unmapped_accounts.map(acc =>
        `"${acc.account || 'N/A'}","${(acc.account_name || '').replace(/"/g, '""')}","${(acc.description || 'No description').replace(/"/g, '""')}",${acc.total || 0}`
      )
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unmapped_accounts_${selectedGl}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filteredAccounts = validationData?.unmapped_accounts?.filter(acc => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (acc.account || '').toLowerCase().includes(search) ||
      (acc.description || '').toLowerCase().includes(search)
    );
  }) || [];

  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    if (sortBy === 'amount') {
      return Math.abs(b.total || 0) - Math.abs(a.total || 0);
    } else {
      return (a.account || '').localeCompare(b.account || '');
    }
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header */}
      <div className="reconcile-header" style={{ marginBottom: '2rem', padding: '1.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="reconcile-title">Unmapped Accounts</div>
        <div className="reconcile-meta">
          <div className="reconcile-meta-item">
            <AlertCircle style={{ width: '1rem', height: '1rem' }} />
            <span className="reconcile-meta-label">Validation:</span>
            <span>Identify accounts not captured by rules</span>
          </div>
        </div>
      </div>

      {/* GL Selection */}
      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="pro-card-title">Select GL Dataset</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
<button
                className="pro-btn pro-btn-primary"
                onClick={() => validateGL(selectedGl)}
                disabled={loading || !selectedGl}
              >
                <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                {loading ? 'Validating...' : 'Run Validation'}
              </button>
            </div>
          </div>
        </div>
        <div className="pro-card-content">
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
              GL Dataset:
            </label>
            <select
              value={selectedGl}
              onChange={(e) => handleGlChange(e.target.value)}
              className="pro-input"
              style={{ maxWidth: '500px' }}
            >
              <option value="">-- Select a GL dataset --</option>
              {glList.map((gl, i) => (
                <option key={i} value={gl.gl_id}>
                  {gl.filename || gl.sheet_name || 'GL'} • {gl.row_count?.toLocaleString() || 0} rows
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Validation Summary */}
      {validationData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
<div className="pro-stat-card">
              <div className="pro-stat-value" style={{ color: validationData.unmapped_accounts.length > 0 ? 'var(--warning-amber)' : 'var(--success-green)' }}>
                {validationData.unmapped_accounts.length}
              </div>
              <div className="pro-stat-label">Unmapped Accounts</div>
            </div>

            <div className="pro-stat-card">
              <div className="pro-stat-value" style={{ color: 'var(--secondary-blue)' }}>
                ${Math.abs(validationData.unmapped_accounts.reduce((sum, acc) => sum + (acc.total || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="pro-stat-label">Total Unmapped Amount</div>
            </div>
          </div>

          {/* Unmapped Accounts Table */}
          {validationData.unmapped_accounts.length > 0 && (
            <div className="pro-card">
              <div className="pro-card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="pro-card-title">Unmapped Account Details ({sortedAccounts.length} accounts)</h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="pro-btn pro-btn-secondary" onClick={exportUnmapped}>
                      <Download style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>
              <div className="pro-card-content">
                {/* Search and Sort Controls */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search accounts or descriptions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pro-input"
                    />
                    <Search style={{ width: '1rem', height: '1rem', color: 'var(--gray-400)', position: 'absolute', right: '0.75rem', top: '0.625rem', pointerEvents: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.875rem', color: 'var(--gray-700)', fontWeight: '500' }}>Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="pro-input"
                      style={{ width: 'auto', minWidth: '150px' }}
                    >
                      <option value="amount">Amount (High to Low)</option>
                      <option value="account">Account Number</option>
                    </select>
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
                  <table className="financial-table" style={{ margin: 0, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '1rem', textAlign: 'left' }}>ACCOUNT NUMBER</th>
                        <th style={{ padding: '1rem', textAlign: 'left' }}>ACCOUNT NAME</th>
                        <th style={{ padding: '1rem', textAlign: 'left' }}>DESCRIPTION</th>
                        <th style={{ padding: '1rem', textAlign: 'right' }}>TOTAL AMOUNT</th>
                        <th style={{ padding: '1rem', textAlign: 'center' }}>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAccounts.map((acc, i) => (
                        <tr key={i}>
                          <td style={{ padding: '1rem', fontFamily: 'monospace', fontWeight: '600', color: 'var(--gray-900)' }}>
                            {acc.account || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', color: 'var(--gray-700)' }}>
                            {acc.account_name || '—'}
                          </td>
                          <td style={{ padding: '1rem', color: 'var(--gray-700)' }}>
                            {acc.description || 'No description'}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: 'var(--gray-900)' }}>
                            ${(acc.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <button
                              className="pro-btn pro-btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                              onClick={() => {
                                // TODO: Open mapping dialog
                                alert('Mapping feature coming soon! For now, add this account to your Rules.xlsx file.');
                              }}
                            >
                              Map Account
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {sortedAccounts.length === 0 && searchTerm && (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
                    <p>No accounts found matching "{searchTerm}"</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {validationData.unmapped_accounts.length === 0 && (
            <div className="pro-card">
              <div className="pro-card-content" style={{ textAlign: 'center', padding: '3rem' }}>
                <CheckCircle style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: 'var(--success-green)' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
                  All Accounts Mapped!
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                  Every account in your GL is covered by your rules. No unmapped accounts found.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {!validationData && !loading && (
        <div className="pro-card">
          <div className="pro-card-content" style={{ textAlign: 'center', padding: '3rem' }}>
            <AlertCircle style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: 'var(--gray-300)' }} />
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              Select a GL dataset and click "Run Validation" to identify unmapped accounts.
            </p>
          </div>
        </div>
      )}

      {validationData && validationData.message && (
        <div className="pro-card">
          <div className="pro-card-content">
            <div style={{ padding: '1rem', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
              <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: '#92400E', flexShrink: 0, marginTop: '0.125rem' }} />
              <div>
                <p style={{ fontSize: '0.875rem', color: '#92400E', fontWeight: '600', marginBottom: '0.25rem' }}>
                  Rules Required
                </p>
                <p style={{ fontSize: '0.875rem', color: '#92400E' }}>
                  {validationData.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug / Diagnostics Panel */}
      {showDebug && (
        <div className="pro-card" style={{ marginTop: '2rem', border: '1px solid #6366F1' }}>
          <div className="pro-card-header" style={{ background: '#EEF2FF' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="pro-card-title" style={{ color: '#4338CA' }}>
                <Bug style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />
                Rules Coverage Diagnostics
              </h3>
              <button className="pro-btn pro-btn-secondary" style={{ fontSize: '0.75rem' }} onClick={() => setShowDebug(false)}>
                Close
              </button>
            </div>
          </div>
          <div className="pro-card-content">
            {debugData ? (
              <>
                {debugData.sample_gl_entry_fields && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ fontSize: '0.8rem' }}>GL Field Names (from sample doc):</strong>
                    <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '4px', padding: '0.5rem', marginTop: '0.25rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {debugData.sample_gl_entry_fields.join(', ') || '(none)'}
                    </div>
                  </div>
                )}
                {debugData.covered_keys_count !== undefined && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ fontSize: '0.8rem' }}>Covered Keys: {debugData.covered_keys_count}</strong>
                    <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '4px', padding: '0.5rem', marginTop: '0.25rem', fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: '100px', overflowY: 'auto' }}>
                      {(debugData.sample_covered_keys || []).join(', ') || '(empty — rules not matching any GL entries)'}
                    </div>
                  </div>
                )}
                <div>
                  <strong style={{ fontSize: '0.8rem' }}>Full Debug Response:</strong>
                  <pre style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '4px', padding: '0.75rem', marginTop: '0.25rem', fontSize: '0.7rem', maxHeight: '300px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(debugData, null, 2)}
                  </pre>
                </div>
              </>
            ) : (
              <p style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6B7280' }}>No data returned.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnmappedAccountsPage;
